import React, { useEffect, useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import FileIcon from '@mui/icons-material/Description';
import PdfIcon from '@mui/icons-material/PictureAsPdf';

import { ReactComponent as ExcelIcon } from '../../../assets/icons/file-preview/excel.svg';
import { ReactComponent as PowerpointIcon } from '../../../assets/icons/file-preview/powerpoint.svg';
import { ReactComponent as WordIcon } from '../../../assets/icons/file-preview/word.svg';
import { useLazyGetDownloadURL } from '../../../redux/api/fireBaseStorageApi';
import { IAppFile } from '../../../types/interfaces/app-file';
import { INewFile } from '../../../types/interfaces/new-file';
import {
  checkIsExcelFile,
  checkIsImage,
  checkIsPdfFile,
  checkIsPowerpointFile,
  checkIsVideo,
  checkIsWordFile,
} from '../../../utils/files/file-utils';
import { useStyles } from './AppFile.styles';
import InteractiveFilePreview from './InteractiveFilePreview/InteractiveFilePreview';

type File = IAppFile | INewFile;

interface AppFileProps {
  file: File;
  onDelete?: (file: File) => void;
  onUpload?: (file: File) => void;
  onClick?: () => void;
}

export default function AppFile(props: AppFileProps) {
  const { file, onDelete, onUpload, onClick = () => {} } = props;
  const [fileSrc, setFileSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [getFileUrl] = useLazyGetDownloadURL();

  const theme = useTheme();

  const { classes } = useStyles(theme);

  useEffect(() => {
    if ('fileBlob' in file) {
      setFileSrc(file.fileBlob);
    } else if ('storageLink' in file) {
      setIsLoading(true);
      getFileUrl(
        {
          storageLink: file.previewStorageLink || file.storageLink,
          directLink: file.directPreviewStorageLink || file.directStorageLink,
        },
        true,
      )
        .unwrap()
        .then((url) => setFileSrc(url))
        .catch(() => setFileSrc(''))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [file]);

  const getImageWrapper = (component: React.ReactElement) => {
    return <div className={classes.imageWrapper}>{component}</div>;
  };

  function getFilePreview() {
    if (checkIsImage(file.mimeType)) {
      return <InteractiveFilePreview fileSrc={fileSrc} alt="Img preview" />;
    }

    if (checkIsVideo(file.mimeType)) {
      return (
        <InteractiveFilePreview
          fileSrc={fileSrc}
          type="video"
          alt="Video preview"
        />
      );
    }

    if (checkIsWordFile(file.mimeType)) {
      return getImageWrapper(<WordIcon />);
    }

    if (checkIsExcelFile(file.mimeType)) {
      return getImageWrapper(<ExcelIcon />);
    }

    if (checkIsPowerpointFile(file.mimeType)) {
      return getImageWrapper(<PowerpointIcon />);
    }

    if (checkIsPdfFile(file.mimeType)) {
      return getImageWrapper(<PdfIcon />);
    }

    return getImageWrapper(<FileIcon />);
  }

  return (
    <div className={classes.fileContainer} onClick={onClick}>
      <div className={classes.fileWrapper}>
        <div className={classes.img}>
          {isLoading ? (
            <div className={classes.loader}>
              <CircularProgress color="primary" />
            </div>
          ) : (
            <>
              {getFilePreview()}
              {/*<div className={classes.footer}>*/}
              {/*  <div className={classes.fileName}>{file.name}</div>*/}
              {/*  <div className={classes.createdAt}>*/}
              {/*    {timeAgo(file.createdAt)}*/}
              {/*  </div>*/}
              {/*</div>*/}
            </>
          )}
        </div>
      </div>
      {(onDelete || onUpload) && (
        <div className={classes.buttonsContainer}>
          {onUpload && (
            <UploadIcon
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onUpload(file);
              }}
            />
          )}
          {onDelete && (
            <CloseIcon
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(file);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
